<template>
        <div
        class="w-full rounded-lg flex flex-col justify-center items-center p-8 mt-6"
    >
        <HeaderIcon>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="#5F6D7E" style="opacity:1;"><path  d="M17.084 15.812a7 7 0 1 0-10.168 0A6 6 0 0 1 12 13a6 6 0 0 1 5.084 2.812M12 23.728l-6.364-6.364a9 9 0 1 1 12.728 0zM12 12a3 3 0 1 1 0-6a3 3 0 0 1 0 6"/></svg>
        </HeaderIcon>

        <div class="w-full max-w-lg flex flex-col justify-center items-center mt-8 z-50">
            <Intro :heading-text="headingText" :sub-text="subText" />


            <div class="w-full max-w-sm mt-6">
                <div class="mb-4">
                    <label
                        for="password"
                        class="block text-sm font-semibold text-gray-700"
                        >Password</label
                    >
                    <input
                        id="password"
                        type="password"
                        v-model="formData.password"
                        required
                        autocomplete="current-password"
                        @input="checkPasswordStrength"
                        class="mt-1 bg-white block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-[#41AE49] focus:border-[#41AE49]"
                    />
                    <p v-for="error in getValidationErrors('password')" :key="error" class="text-red-500 text-sm">
                        {{ error }}
                    </p>
                </div>
                <div class="mb-4">
                    <label
                        for="password_confirmed"
                        class="block text-sm font-semibold text-gray-700"
                        >Confirm Password</label
                    >
                    <input
                        id="password_confirmed"
                        type="password"
                        v-model="formData.password_confirmation"
                        required
                        class="mt-1 bg-white block w-full px-4 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-[#41AE49] focus:border-[#41AE49]"
                    />
                    <p v-for="error in getValidationErrors('password_confirmation')" :key="error" class="text-red-500 text-sm">
                        {{ error }}
                    </p>

                    <div class="flex mt-2 space-x-1">
                        <div
                            v-for="n in 4"
                            :key="n"
                            :class="[
                                'h-1 flex-1 rounded',
                                strength >= n
                                    ? strengthColors[strength]
                                    : 'bg-gray-200',
                            ]"
                        ></div>
                    </div>
                </div>

                <div
                    class="flex flex-wrap items-center gap-1 my-5 text-sm text-gray-500"
                >
                <div class="flex items-center gap-2">
                    <Checkbox
                        id="accept_terms"
                        v-model="formData.accept_terms"
                        @change.stop="toggleAcceptTerms"
                    />
                    <label for="accept_terms" class="block text-sm text-primary-gray">
                        I agree to the 
                        <a class="font-medium text-primary-font underline" :href="termsUrl" target="_blank">Terms & Conditions</a> and
                        <a class="font-medium text-primary-font underline" :href="privacyPolicyUrl" target="_blank">Privacy Policy</a>
                    </label>
                </div>
                <p v-for="error in getValidationErrors('accept_terms')" :key="error" class="text-red-500 text-sm">
                    {{ error }}
                </p>
            </div>
                <button
                    v-if="formData.market_id"
                    :disabled="isLoading"
                    type="submit"
                    @click.stop="submitPassword"
                    class="w-full py-2 px-4 bg-[#41AE49] text-white font-semibold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-[#41AE49]"
                >
                    <span v-if="isLoading">Processing...</span>
                    <span v-else>Create Password</span>
                </button>


                <button
                    v-else
                    :disabled="isLoading"
                    type="submit"
                    @click.stop="submitPassword"
                    class="w-full py-2 px-4 bg-[#41AE49] text-white font-semibold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-[#41AE49]"
                >
                    <span v-if="isLoading">Processing...</span>
                    <span v-else>Continue</span>
                </button>

                
            </div>

           
        </div>
    </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import HeaderIcon from "./components/HeaderIcon.vue";
import Intro from "./components/Intro.vue";
import { useSignupWizardStore } from "@/store";
import { storeToRefs } from "pinia";
import Checkbox from "@/components/ui/checkbox/Checkbox.vue";
import { useConfig } from "@/composables";

const signupWizardStore = useSignupWizardStore();

const { formData, isLoading } = storeToRefs(signupWizardStore);
const {fetchSignupState, getValidationErrors, toggleAcceptTerms, submitPassword} = signupWizardStore;

const { termsUrl, privacyPolicyUrl } = useConfig();

const headingText = "Password Setup";
const subText = "Set up a secure password to protect your account.";

const strength = ref(0);

const strengthColors = {
    1: "bg-red-500",
    2: "bg-yellow-500",
    3: "bg-blue-500",
    4: "bg-green-500",
};

function checkPasswordStrength() {
    const value = formData.value.password;
    let score = 0;

    if (value.length >= 6) score++;
    if (/[A-Z]/.test(value)) score++;
    if (/\d/.test(value)) score++;
    if (/[^A-Za-z0-9]/.test(value)) score++;

    strength.value = score;
}

onMounted(async () => {
    await fetchSignupState();
})
</script>
